import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, ArrowsDownUp, CaretUp, CaretDown, PencilSimple, PaperPlaneTilt } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PlanDetail as PlanDetailComponent } from '@/pages/PlanosPage';
import { promptEnableReminders } from '@/lib/remindersPrompt';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { GuiaOrientacoes } from '@/components/admin/GuiaOrientacoes';
import { fmtDay, fmtStamp } from '@/utils/dateFmt';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

function PlanosTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [mealSlots, setMealSlots] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [planName, setPlanName] = useState('');
  const [importFromId, setImportFromId] = useState('');
  const [creating, setCreating] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [notifiedAt, setNotifiedAt] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const fetch = useCallback(async () => {
    try {
      const [pRes, sRes, nRes] = await Promise.all([
        axios.get(`${API}/api/meal-plans?client_id=${patientId}`),
        axios.get(`${API}/api/patients/${patientId}/plan-snapshots`),
        axios.get(`${API}/api/patients/${patientId}/plan-notification`).catch(() => ({ data: {} })),
      ]);
      setPlans(pRes.data);
      setSnapshots(sRes.data || []);
      setNotifiedAt((nRes.data && nRes.data.last_notified_at) || null);
    } catch { }
  }, [patientId]);
  const notifyPlan = async () => {
    if (!window.confirm('Enviar email à paciente a avisar que o plano está disponível?')) return;
    setNotifying(true);
    try {
      const { data } = await axios.post(`${API}/api/patients/${patientId}/notify-plan`, {});
      if (data && data.ok === false) { toast.error(data.error || 'Falha no envio do email'); return; }
      setNotifiedAt(data.last_notified_at);
      toast.success('Paciente notificada por email');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao notificar'); }
    finally { setNotifying(false); }
  };
  const fetchMealTypes = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/meal-types`); setMealSlots(data.types || []); } catch { }
  }, []);
  useEffect(() => { fetch(); fetchMealTypes(); }, [fetch, fetchMealTypes]);
  const resetCreateForm = () => { setShowCreate(false); setPlanName(''); setImportFromId(''); };
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      let data;
      if (importFromId) {
        const res = await axios.post(`${API}/api/patients/${patientId}/meal-plans/import`, { source_plan_id: importFromId, name: planName });
        data = res.data;
        toast.success('Plano criado a partir do plano anterior');
      } else {
        const res = await axios.post(`${API}/api/meal-plans`, { client_id: patientId, name: planName, status: 'rascunho', meals: {} });
        data = res.data;
        toast.success('Plano criado');
      }
      resetCreateForm();
      promptEnableReminders(patientId, 'meals');
      // Open the new plan directly for editing
      setSelectedPlan(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setCreating(false); }
  };
  const handleDeletePlan = async (p) => {
    if (p.status === 'ativo') { toast.error('Desativa o plano antes de o eliminar.'); return; }
    const ok = await confirmDelete({ description: `Vai apagar o plano alimentar "${p.name}". Esta ação é irreversível.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/meal-plans/${p.id}`); toast.success('Plano eliminado'); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao eliminar'); }
  };
  // When a plan is selected, show PlanDetail inline (stays in patient context)
  // key={selectedPlan.id} força o React a desmontar/remontar por completo ao trocar de plano,
  // evitando qualquer estado interno (versão ativa, edições em curso) do plano anterior persistir.
  if (selectedPlan) {
    return (
      <div data-testid="tab-planos-detail">
        <PlanDetailComponent
          key={selectedPlan.id}
          plan={selectedPlan}
          onBack={() => { setSelectedPlan(null); fetch(); }}
          onUpdate={() => { fetch(); }}
          isNutri={true}
          defaultMealTypes={mealSlots}
        />
      </div>
    );
  }
  return (
    <div data-testid="tab-planos">
      <GuiaOrientacoes patientId={patientId} />
      <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={notifyPlan} disabled={notifying} className="border border-brand-green text-brand-green rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1 disabled:opacity-50 hover:bg-brand-mint/10" data-testid="notify-plan-btn"><PaperPlaneTilt size={14} /> {notifying ? 'A enviar...' : 'Notificar paciente'}</button>
          {notifiedAt && <span className="text-[11px] text-brand-muted" data-testid="notify-plan-last">Último envio: {fmtStamp(notifiedAt)}</span>}
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="create-plan-inline"><Plus size={14} /> Novo plano</button>
      </div>
      {(() => {
        const activePlans = plans.filter(p => p.status !== 'arquivado');
        const archivedPlans = plans.filter(p => p.status === 'arquivado');
        return (
          <>
            {activePlans.length === 0 ? <p className="text-center text-brand-muted py-8">Sem planos alimentares ativos.</p> : (
              <div className="space-y-2">
                {activePlans.map(p => (
                  <div key={p.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between hover:border-brand-green/30 transition-all">
                    <button onClick={() => setSelectedPlan(p)} className="flex-1 text-left">
                      <p className="font-medium text-sm text-brand-text">{p.name}</p>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${p.status === 'ativo' ? 'bg-brand-mint/20 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}>{p.status}</span>
                    </button>
                    <div className="flex gap-1">
                      <button onClick={() => setSelectedPlan(p)} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={14} /></button>
                      <button onClick={() => handleDeletePlan(p)} disabled={p.status === 'ativo'} title={p.status === 'ativo' ? 'Desativa o plano antes de eliminar' : 'Eliminar plano'} className="p-1 text-brand-placeholder hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`delete-plan-${p.id}`}><Trash size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {archivedPlans.length > 0 && (
              <div className="mt-6" data-testid="plan-history-section">
                <h4 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">Histórico de planos ({archivedPlans.length})</h4>
                <div className="space-y-1.5">
                  {archivedPlans.map(p => (
                    <div key={p.id} className="bg-brand-bg/40 border border-brand-border/60 rounded-xl p-3 flex items-center justify-between" data-testid={`archived-plan-${p.id}`}>
                      <button onClick={() => setSelectedPlan(p)} className="flex-1 text-left">
                        <p className="text-xs font-medium text-brand-text">{p.name}</p>
                        <p className="text-[10px] text-brand-muted">
                          Criado em {p.created_at ? fmtDay(p.created_at) : '?'}
                          {p.archived_at ? ` · arquivado em ${fmtDay(p.archived_at)}` : ''}
                        </p>
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] bg-brand-placeholder/20 text-brand-muted px-2 py-0.5 rounded-full">arquivado</span>
                        <button onClick={() => handleDeletePlan(p)} className="p-1 text-brand-placeholder hover:text-red-500" title="Eliminar plano" data-testid={`delete-archived-plan-${p.id}`}><Trash size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}
      {/* Plan snapshots history */}
      {snapshots.length > 0 && (
        <div className="mt-5 bg-white border border-brand-border rounded-2xl p-4" data-testid="plan-snapshots">
          <h4 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-2">Historial de planos</h4>
          <div className="space-y-1.5">
            {snapshots.map(s => (
              <div key={s.id} className="flex items-center justify-between text-xs bg-brand-bg/30 rounded-xl px-3 py-2">
                <div>
                  <span className="font-medium text-brand-text">{s.plan_name}</span>
                  <span className="text-brand-muted ml-2">{fmtDatePt(s.date)}</span>
                </div>
                <div className="flex gap-3 text-[10px] text-brand-muted">
                  <span>{s.kcal} kcal</span>
                  <span>P:{s.proteina_g}g</span>
                  <span>HC:{s.hidratos_g}g</span>
                  <span>G:{s.gordura_g}g</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Dialog open={showCreate} onOpenChange={(v) => { if (!v) resetCreateForm(); else setShowCreate(true); }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Novo Plano para este paciente</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome do plano</Label><Input value={planName} onChange={e => setPlanName(e.target.value)} className="rounded-xl border-brand-border" required data-testid="inline-plan-name" /></div>
            {plans.length > 0 && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Importar de plano anterior (opcional)</Label>
                <select value={importFromId} onChange={e => setImportFromId(e.target.value)} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="import-from-plan-select">
                  <option value="">Começar em branco</option>
                  {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <p className="text-[10px] text-brand-muted mt-1">O plano escolhido é copiado para este novo plano (refeições, alimentos e secções), sem alterar o original.</p>
              </div>
            )}
            <button type="submit" disabled={creating} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="submit-inline-plan">{creating ? 'A criar...' : 'Criar plano'}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PlanosTab;
